<template>
  <b-modal 
    id="modal-upload-completion" 
    centered 
    hide-footer 
    title="Upload Sertifikat"
    size="lg">
    <div class="p-4">
      <!-- Participant Info -->
      <div class="participant-info-section mb-4 p-3 rounded" style="background-color: #fafafa;">
        <div class="d-flex align-items-center">
          <div class="participant-icon me-3">
            <i class="fe fe-user text-danger" style="font-size: 1.5rem;"></i>
          </div>
          <div>
            <h6 class="mb-1 font-weight-bold">{{ selectedParticipant?.fullname || selectedParticipant?.name || '-' }}</h6>
            <small class="text-muted">
              <i class="fe fe-briefcase me-1"></i>
              {{ selectedParticipant?.position_level || selectedParticipant?.position || '-' }} • {{ selectedParticipant?.nik || '-' }}
            </small>
          </div>
        </div>
      </div>

      <div v-if="isBeforeTrainingEndDate" class="alert alert-warning d-flex align-items-start" role="alert">
        <i class="fe fe-alert-triangle me-2" style="font-size: 1.1rem;"></i>
        <div>
          Sertifikat dapat diupload pada atau setelah {{ formattedTrainingEndDate }}.
        </div>
      </div>

      <!-- Upload Section -->
      <div class="upload-section mb-4">
        <label class="form-label font-weight-bold">
          File Sertifikat <span class="text-danger">*</span>
        </label>
        <p class="text-muted small mb-2">
          Upload sertifikat dalam format gambar atau PDF (JPG, JPEG, PNG, PDF). Maksimal 5MB.
        </p>
        
        <!-- Dropzone -->
        <div 
          class="dropzone p-4 border rounded text-center"
          :class="{ 'border-danger': isDragging, 'is-invalid': !selectedFile && submitted, 'is-disabled': isBeforeTrainingEndDate }"
          @dragover.prevent="handleDragOver"
          @dragleave.prevent="handleDragLeave"
          @drop.prevent="handleDrop">
          
          <!-- File Belum Dipilih -->
          <div v-if="!selectedFile && !previewUrl">
            <i class="fe fe-upload-cloud text-muted mb-2" style="font-size: 3rem;"></i>
            <p class="text-muted mb-2">Tarik file sertifikat ke sini atau</p>
            <label class="btn btn-outline-danger btn-sm">
              Pilih File
              <input 
                type="file" 
                accept="image/jpeg,image/jpg,image/png,application/pdf"
                :disabled="isBeforeTrainingEndDate"
                @change="handleFileSelect"
                hidden>
            </label>
          </div>

          <!-- File Selected / Preview -->
          <div v-else>
            <!-- Image Preview -->
            <img 
              v-if="previewUrl && !isPdfFile"
              :src="previewUrl" 
              alt="Preview Sertifikat"
              class="img-fluid rounded mb-3"
              style="max-height: 250px;">
            <!-- PDF Preview -->
            <div v-else-if="isPdfFile" class="pdf-preview mb-3">
              <div class="pdf-icon-container">
                <i class="fe fe-file-text"></i>
              </div>
              <p class="pdf-label mb-0">File PDF</p>
            </div>
            <div class="d-flex align-items-center justify-content-center gap-2">
              <span class="text-success">
                <i class="fe fe-check-circle me-1"></i>
                {{ selectedFile?.name }}
              </span>
              <button 
                class="btn btn-outline-danger btn-sm"
                @click="removeFile">
                <i class="fe fe-x"></i>
              </button>
            </div>
            <p class="text-muted small mt-2 mb-0">
              Ukuran: {{ formatFileSize(selectedFile?.size) }}
            </p>
          </div>
        </div>
        <small v-if="!selectedFile && submitted" class="text-danger">
          Pilih file sertifikat terlebih dahulu.
        </small>
      </div>

      <!-- Note Section -->
      <div class="note-section mb-4">
        <label class="form-label font-weight-bold">
          Catatan <span class="text-muted">(Opsional)</span>
        </label>
        <textarea 
          v-model="note"
          class="form-control"
          rows="3"
          placeholder="Tambahkan catatan terkait sertifikat..."
          maxlength="500">
        </textarea>
        <small class="text-muted">{{ note.length }}/500 karakter</small>
      </div>

      <!-- Info Alert -->
      <div class="alert alert-info d-flex align-items-start" role="alert">
        <i class="fe fe-info me-2" style="font-size: 1.1rem;"></i>
        <div>
          <strong>Catatan:</strong> Setelah diupload, sertifikat akan masuk ke proses review completion.
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="modal-footer border-top">
      <button 
        class="btn btn-secondary"
        @click="closeModal"
        :disabled="isUploading">
        Batal
      </button>
      <button 
        class="btn btn-danger"
        @click="submitUpload"
        :disabled="isUploading || isBeforeTrainingEndDate">
        <span v-if="isUploading" class="spinner-border spinner-border-sm me-1" role="status"></span>
        {{ isUploading ? 'Mengupload...' : 'Submit Sertifikat' }}
      </button>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex';
import { normalizeErrorMessage } from '@/utils/error-message';

export default {
  name: 'ModalUploadCompletion',
  props: {
    selectedParticipant: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      selectedFile: null,
      previewUrl: null,
      isPdfFile: false,
      note: '',
      isDragging: false,
      isUploading: false,
      submitted: false
    }
  },
  computed: {
    isBeforeTrainingEndDate() {
      if (typeof this.selectedParticipant?.is_before_training_end_date === 'boolean') {
        return this.selectedParticipant.is_before_training_end_date;
      }

      const endDateValue = this.selectedParticipant?.training_end_date;
      if (!endDateValue) return false;

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const trainingEndDate = new Date(endDateValue);
      trainingEndDate.setHours(0, 0, 0, 0);

      return today < trainingEndDate;
    },

    formattedTrainingEndDate() {
      const endDateValue = this.selectedParticipant?.training_end_date;
      if (!endDateValue) return '-';

      return new Date(endDateValue).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }
  },
  methods: {
    ...mapActions('courseCompletionMitra', ['submitCompletion']),

    handleDragOver() {
      if (this.isBeforeTrainingEndDate) return;
      this.isDragging = true;
    },

    handleDragLeave() {
      this.isDragging = false;
    },

    handleFileSelect(event) {
      if (this.isBeforeTrainingEndDate) return;
      const file = event.target.files[0];
      this.processFile(file);
    },

    handleDrop(event) {
      this.isDragging = false;
      if (this.isBeforeTrainingEndDate) return;
      const file = event.dataTransfer.files[0];
      this.processFile(file);
    },

    processFile(file) {
      if (!file) return;

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        this.$swal({
          title: 'Format File Tidak Sesuai',
          text: 'Upload file gambar (JPG, JPEG, PNG) atau PDF.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        this.$swal({
          title: 'File Terlalu Besar',
          text: 'Ukuran file maksimal 5MB.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
        return;
      }

      this.selectedFile = file;
      this.isPdfFile = file.type === 'application/pdf';
      
      // Only create preview URL for images
      if (!this.isPdfFile) {
        this.previewUrl = URL.createObjectURL(file);
      } else {
        this.previewUrl = null;
      }
    },

    removeFile() {
      this.selectedFile = null;
      this.isPdfFile = false;
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
      }
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    async submitUpload() {
      this.submitted = true;

      if (this.isBeforeTrainingEndDate) {
        this.$swal({
          title: 'Pelatihan Belum Selesai',
          text: `Sertifikat dapat diupload pada atau setelah ${this.formattedTrainingEndDate}.`,
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return;
      }

      if (!this.selectedFile) {
        return;
      }

      if (!this.selectedParticipant?.enrollment_id) {
        this.$swal({
          title: 'Error',
          text: 'Data enrollment tidak ditemukan.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
        return;
      }

      this.isUploading = true;

      try {
        const formData = new FormData();
        formData.append('file', this.selectedFile);
        if (this.note) {
          formData.append('note', this.note);
        }

        await this.submitCompletion({
          enrollmentId: this.selectedParticipant.enrollment_id,
          formData
        });

        this.$swal({
          title: 'Success',
          text: 'Sertifikat berhasil dikirim untuk review completion.',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#00d97e'
        }).then(() => {
          this.$emit('completion-submitted');
          this.resetForm();
        });
      } catch (error) {
        console.error('Error submitting certificate:', error);
        this.$swal({
          title: 'Error',
          text: normalizeErrorMessage(error.message || 'Sertifikat belum berhasil dikirim'),
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        this.isUploading = false;
      }
    },

    closeModal() {
      this.resetForm();
      this.$bvModal.hide('modal-upload-completion');
    },

    resetForm() {
      this.removeFile();
      this.note = '';
      this.submitted = false;
    }
  },

  beforeDestroy() {
    // Clean up object URL
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
  }
}
</script>

<style scoped>
.dropzone {
  background-color: #f8f9fa;
  border: 2px dashed #dee2e6 !important;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dropzone:hover,
.dropzone.border-danger {
  border-color: #e63757 !important;
  background-color: #fff5f7;
}

.dropzone.is-invalid {
  border-color: #dc3545 !important;
}

.dropzone.is-disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.participant-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #fce4e4;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gap-2 {
  gap: 0.5rem;
}

.me-1 {
  margin-right: 0.25rem;
}

.me-2 {
  margin-right: 0.5rem;
}

.me-3 {
  margin-right: 1rem;
}

/* PDF Preview Styles */
.pdf-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.pdf-preview .pdf-icon-container {
  width: 80px;
  height: 100px;
  background: linear-gradient(135deg, #e63757 0%, #c82333 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(230, 55, 87, 0.3);
}

.pdf-preview .pdf-icon-container i {
  font-size: 2.5rem;
  color: #fff;
}

.pdf-preview .pdf-label {
  margin-top: 1rem;
  font-weight: 600;
  color: #1a202c;
}
</style>
